import { useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogSectionHeader } from '../CatalogSectionHeader';
import { UnitFormDialog, type UnitFormValues } from './UnitFormDialog';
import {
  CREATE_UNIT_MUTATION,
  SET_UNIT_STATUS_MUTATION,
  UNITS_QUERY,
  UPDATE_UNIT_MUTATION,
} from './units.gql';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface Unit {
  id: string;
  code: string;
  name: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface UnitsResponse {
  units: {
    items: Unit[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function UnitsPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_manage = user?.permissions.includes('admin.catalog.manage') ?? false;

  const [editing, setEditing] = useState<Unit | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, loading, refetch } = useQuery<UnitsResponse>(UNITS_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
    },
  });

  const [createUnit, { loading: creating_loading }] = useMutation(CREATE_UNIT_MUTATION);
  const [updateUnit, { loading: updating_loading }] = useMutation(UPDATE_UNIT_MUTATION);
  const [setStatus] = useMutation(SET_UNIT_STATUS_MUTATION);

  const onSubmitCreateUnit = async (values: UnitFormValues): Promise<void> => {
    try {
      const result = await createUnit({ variables: { input: values } });
      assertMutationSucceeded(result, 'create_unit_failed');
      setCreating(false);
      showToast({
        tone: 'success',
        title: t('alerts.create_success_title'),
        body: t('alerts.create_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.create_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onSubmitUpdateUnit = async (values: UnitFormValues): Promise<void> => {
    if (!editing) return;
    try {
      const result = await updateUnit({ variables: { id: editing.id, input: values } });
      assertMutationSucceeded(result, 'update_unit_failed');
      setEditing(null);
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const onClickToggleStatus = async (row: Unit): Promise<void> => {
    try {
      const result = await setStatus({
        variables: {
          id: row.id,
          status: row.status === 'active' ? 'inactive' : 'active',
        },
      });
      assertMutationSucceeded(result, 'update_unit_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const columns: CrudColumn<Unit>[] = [
    {
      key: 'code',
      header_key: 'catalog.units.code',
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
      width: 'w-32',
    },
    { key: 'name', header_key: 'catalog.units.name', render: (r) => r.name },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) =>
        can_manage ? (
          <div className="flex justify-end gap-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(r)}>
              {t('common.edit')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onClickToggleStatus(r);
              }}
            >
              {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <CatalogSectionHeader
        title={t('catalog.units.title')}
        subtitle={t('catalog.units.subtitle')}
      />

      <CrudTable<Unit>
        rows={data?.units.items ?? []}
        columns={columns}
        loading={loading}
        page={data?.units.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="catalog.units.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            primary_action={
              can_manage ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('catalog.units.create')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />

      {creating ? (
        <UnitFormDialog
          title={t('catalog.units.create')}
          submitting={creating_loading}
          onSubmit={onSubmitCreateUnit}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <UnitFormDialog
          title={t('catalog.units.edit')}
          initial_values={{ code: editing.code, name: editing.name }}
          submitting={updating_loading}
          onSubmit={onSubmitUpdateUnit}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
